import { memo } from 'react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface ProfileSkeletonProps {
  className?: string;
}

/**
 * Skeleton matching ProfilePage header dimensions exactly to prevent layout shift.
 * Includes: cover image, avatar, name, bio, subscription card, and form fields.
 */
export const ProfileSkeleton = memo(function ProfileSkeleton({
  className,
}: ProfileSkeletonProps) {
  return (
    <div className={cn('space-y-6', className)}>
      {/* Cover image area - matches h-32 */}
      <Skeleton className="w-full h-32 rounded-none" />
      
      {/* Avatar + Name section - offset from cover */}
      <div className="px-4 -mt-12">
        <div className="flex flex-col items-center mb-6">
          {/* Avatar - matches w-24 h-24 */}
          <Skeleton className="w-24 h-24 rounded-full border-4 border-background" />
          
          {/* Name and bio */}
          <div className="text-center mt-3 space-y-2">
            <Skeleton className="h-5 w-32 mx-auto" />
            <Skeleton className="h-4 w-48 mx-auto" />
          </div>
        </div>
        
        {/* Subscription card skeleton */}
        <div className="rounded-2xl border border-border p-4 mb-6 space-y-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-6 w-16 rounded-full" />
          </div>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-10 w-full rounded-xl" />
        </div>
        
        {/* Form fields */}
        <div className="space-y-6">
          {/* Display name field */}
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
          
          {/* Bio field */}
          <div className="space-y-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-24 w-full rounded-md" />
          </div>
          
          {/* Social links section */}
          <div className="space-y-3">
            <Skeleton className="h-4 w-28" />
            <div className="grid grid-cols-3 gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-12 rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ProfileSkeleton;
